import {Form, Spin} from 'antd';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import v2 from '../v2/instrumentV2.module.scss';
import {InstrumentActions} from '../v2/InstrumentActions';
import {
  OfferingFields,
  buildOffering,
  offeringToFormValues,
} from '../v2/OfferingFields';
import {IPO_OFFERING_MOCK} from '../v2/devPrefill';
import {handleIpoInstruments} from 'redux/store/assetClass';
import {GET_IPO_BY_ID} from 'graphql/queries/assetManagement';
import {UPDATE_IPO} from 'graphql/mutations/assetManagement';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import DBModal from 'components/modal/modal';
import {CreateIpoFinishModal} from './CreateIpoFinishModal';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {handleUpdateIpo} from '../actions';

/**
 * Who is bringing the IPO to market, and what the proceeds fund.
 *
 * Final screen of the IPO create flow — submitting here opens the create
 * modal. `offering` sits on the base instrument input rather than on IpoInput,
 * so this screen writes through the base draft.
 */
export const IpoOffering = () => {
  const [form] = Form.useForm();
  const {id, lang} = useParams();
  const dispatch = useDispatch();
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [updatedValues, setUpdatedValues] = useState({});

  const draft = useSelector(state => state?.assetClass?.ipoInstruments);
  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  const [updateIpo, {loading: loadingUpdate}] = useMutation(UPDATE_IPO);
  const {
    data: fetched,
    refetch,
    loading,
  } = useQuery(GET_IPO_BY_ID, {
    // The language versions table links here with the variant's language in
    // the URL; without passing it the read always came back in the base
    // language and the form silently showed the wrong translation.
    variables: {getFinancialInstrumentId: id, language: lang},
    skip: !id,
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const applyOffering = offering => {
    if (!offering) return;
    form.setFieldsValue(offeringToFormValues(offering));
  };

  useEffect(() => {
    if (id) applyOffering(fetched?.getFinancialInstrument?.data?.offering);
  }, [fetched?.getFinancialInstrument?.data]);

  useEffect(() => {
    if (!id) applyOffering(draft?.offering);
  }, [draft]);

  const handleSubmit = values => {
    const offering = buildOffering(values);
    if (id) {
      // `offering` belongs to the base instrument input, not UpdateIpoInput.
      setUpdatedValues({baseFinancialInstrumentInput: {offering}});
      setOpenUpdateModal(true);
    } else {
      dispatch(handleIpoInstruments({offering}));
      setOpenCreateModal(true);
    }
  };

  if (loading) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <div>
      <div className={v2.bodyHead}>
        <h2>Offering</h2>
        <p>
          Who is bringing this issue to market, and how the proceeds will be
          used.
        </p>
      </div>

      <Form onFinish={handleSubmit} form={form} layout="vertical">
        <OfferingFields proceedsDescription="What the company will fund with the money raised. Percentages should total 100." />

        <InstrumentActions
          disabled={!pageOperations?.update}
          label={id ? 'Update' : 'Create IPO'}
          onPrepopulate={() => form.setFieldsValue(IPO_OFFERING_MOCK)}
        />
      </Form>

      <DBModal
        isOpen={openCreateModal}
        handleClose={() => setOpenCreateModal(false)}
        content={
          <CreateIpoFinishModal handleClose={() => setOpenCreateModal(false)} />
        }
      />

      <ConfirmModal
        openModal={openUpdateModal}
        setOpenModal={() => setOpenUpdateModal(false)}
        loading={loadingUpdate}
        title="Update IPO"
        confirmText="Are you sure you want to update this offering?"
        handleOperation={() =>
          handleUpdateIpo(
            updateIpo,
            id,
            lang,
            updatedValues,
            refetch,
            setOpenUpdateModal,
            true,
          )
        }
      />
    </div>
  );
};

export default IpoOffering;
